import React, { useCallback } from "react";
import { ButtonProps } from "models/button"; // Type definition for the button props
// import { Button as AntButton } from "antd"; // Ant Design Button
import { BTN_MODE, BTN_SIZE, BTN_VARIANTS } from "constants/components"; // Constants for button modes and sizes
import styles from "./Button.module.scss"; // Styles for custom button
import asyncComponentLoader from "core/loader";

// ARIA roles for different button types
const getAriaRole = (href?: string) => (href ? "link" : "button");
const AntButton = asyncComponentLoader(() =>
  import("antd").then((module) => ({ default: module.Button })),
);
/**
 * Button Component that wraps the Ant Design Button component and allows customization of styles, type, and behavior.
 * @param {ButtonProps} props - The props that control the button's behavior and appearance.
 */
export const Button: React.FC<ButtonProps> = React.memo(
  ({
    onClick,
    onMouseEnter,
    onMouseLeave,
    className = "",
    type = "",
    size = BTN_SIZE.DEFAULT,
    mode = BTN_MODE.NORMAL,
    disabled = false,
    href,
    block,
    loading,
    children,
    target,
    rel,
    title,
    htmlType = "button",
  }) => {
    // Handle Enter key press when button is focused
    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLElement>) => {
        if (
          event.key === "Enter" &&
          !disabled &&
          !loading &&
          event.currentTarget
        ) {
          event.preventDefault();
          const syntheticEvent = new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
            view: window,
          }) as unknown as React.MouseEvent<HTMLElement>;

          // Only call onClick if it exists
          onClick?.(syntheticEvent);
        }
      },
      [onClick, disabled, loading],
    );

    const buttonClass = `${className} ${type ? styles[type] : ""} ${styles[size]}`;

    const commonProps = {
      onClick,
      onMouseEnter,
      onMouseLeave,
      onKeyDown: handleKeyDown,
      disabled,
      href,
      block,
      loading,
      target,
      rel,
      className: buttonClass,
      htmlType,
      role: getAriaRole(href),
      "aria-disabled": disabled,
      "aria-label":
        title || (typeof children === "string" ? children : undefined),
      "aria-busy": loading,
      tabIndex: disabled ? -1 : 0,
      // Add noopener and noreferrer for security when target is _blank
      ...(target === "_blank" && { rel: "noopener noreferrer" }),
    };
    // Combine the base class with dynamic styles for button type and size

    return type === BTN_VARIANTS.NEUTRAL ? (
      <AntButton type="text" {...commonProps}>
        {children}
      </AntButton>
    ) : (
      <AntButton
        type={mode} // Use the `mode` prop for button style (primary, default, etc.)
        {...commonProps}
      >
        {children} {/* Render the children (button text or icon) */}
      </AntButton>
    );
  },
);
